"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "@crehana/i18n";
import { cn } from "../../../utils.js";
import i18n from "../../i18n/index.js";
import { PrimitiveDropdown, PrimitiveDropdownAltContent, PrimitiveDropdownClientSearch, PrimitiveDropdownContent, PrimitiveDropdownEmpty, PrimitiveDropdownList, PrimitiveDropdownTrigger, PrimitiveDropdownValueButton } from "../../primitive-dropdown/primitive-dropdown.js";
function GenericDropdown({ defaultSelected = [], onOpenChange: controlledOnOpenChange, open: controlledOpen, keepOpenOnSelect, loading, className, size, disabled, placeholder, options, removePortal, dropdownContentClassName, showError, onRetry, multiline, ...otherProps }) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;
    const [uncontrolledValues, setUncontrolledValues] = useState(Array.isArray(defaultSelected) ? defaultSelected : [
        defaultSelected
    ]);
    const open = controlledOpen ?? uncontrolledOpen;
    const handleOpenChange = (open)=>{
        if (keepOpenOnSelect) setUncontrolledOpen(open);
        else onOpenChange(open);
    };
    if (!otherProps.multiselect) {
        const selectedValue = otherProps.selected ?? uncontrolledValues[0];
        const { allowDeselect, onSelect: controlledOnSelected } = otherProps;
        return /*#__PURE__*/ jsxs(PrimitiveDropdown, {
            open: open,
            onOpenChange: handleOpenChange,
            children: [
                /*#__PURE__*/ jsx(PrimitiveDropdownTrigger, {
                    asChild: true,
                    children: otherProps.customTriggerButton ?? /*#__PURE__*/ jsx(PrimitiveDropdownValueButton, {
                        className: className,
                        size: size,
                        disabled: disabled,
                        open: open,
                        placeholder: placeholder,
                        value: options.find((option)=>option.value === selectedValue)?.label
                    })
                }),
                /*#__PURE__*/ jsx(PrimitiveDropdownContent, {
                    removePadding: true,
                    removePortal: removePortal,
                    className: cn(multiline && 'tw-max-w-[500px]', dropdownContentClassName),
                    children: /*#__PURE__*/ jsx(PrimitiveDropdownAltContent, {
                        isLoading: loading,
                        isError: showError,
                        onRetry: onRetry,
                        children: /*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                !otherProps.hideSearch && /*#__PURE__*/ jsx(SearchComponents, {
                                    options: options,
                                    ...otherProps
                                }),
                                /*#__PURE__*/ jsx(PrimitiveDropdownList, {
                                    children: options.map(({ element: Element, value })=>/*#__PURE__*/ jsx(Element, {
                                            disabled: disabled,
                                            selected: selectedValue === value,
                                            value: value,
                                            onSelect: ()=>{
                                                if (!keepOpenOnSelect) handleOpenChange(false);
                                                const valueToPropagate = allowDeselect && selectedValue === value ? void 0 : value;
                                                if (controlledOnSelected) {
                                                    if (allowDeselect) controlledOnSelected(valueToPropagate);
                                                    if (!allowDeselect && valueToPropagate) controlledOnSelected(valueToPropagate);
                                                }
                                                setUncontrolledValues(valueToPropagate ? [
                                                    valueToPropagate
                                                ] : []);
                                            }
                                        }, value))
                                })
                            ]
                        })
                    })
                })
            ]
        });
    }
    const values = otherProps.selected ?? uncontrolledValues;
    return /*#__PURE__*/ jsxs(PrimitiveDropdown, {
        open: open,
        onOpenChange: handleOpenChange,
        children: [
            /*#__PURE__*/ jsx(PrimitiveDropdownTrigger, {
                asChild: true,
                children: otherProps.customTriggerButton ?? /*#__PURE__*/ jsx(PrimitiveDropdownValueButton, {
                    className: className,
                    size: size,
                    disabled: disabled,
                    open: open,
                    placeholder: placeholder,
                    value: values.map((value)=>options.find((option)=>option.value === value)?.label).join(', ')
                })
            }),
            /*#__PURE__*/ jsx(PrimitiveDropdownContent, {
                removePadding: true,
                removePortal: removePortal,
                className: cn(multiline && 'tw-max-w-[500px]'),
                children: /*#__PURE__*/ jsx(PrimitiveDropdownAltContent, {
                    isLoading: loading,
                    isError: showError,
                    onRetry: onRetry,
                    children: /*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            !otherProps.hideSearch && /*#__PURE__*/ jsx(SearchComponents, {
                                options: options,
                                ...otherProps
                            }),
                            /*#__PURE__*/ jsx(PrimitiveDropdownList, {
                                children: options.map(({ element: Element, value })=>/*#__PURE__*/ jsx(Element, {
                                        disabled: disabled,
                                        selected: values.includes(value),
                                        value: value,
                                        onSelect: ()=>{
                                            const valuesToPropagate = values.includes(value) ? values.filter((v)=>v !== value) : [
                                                ...values,
                                                value
                                            ];
                                            if (otherProps.onSelect) otherProps.onSelect(valuesToPropagate);
                                            setUncontrolledValues(valuesToPropagate);
                                        }
                                    }, value))
                            })
                        ]
                    })
                })
            })
        ]
    });
}
function SearchComponents({ options, ...otherProps }) {
    if (otherProps.hideSearch) return null;
    const { languageCode } = useTranslation();
    const { searchProps } = otherProps;
    const searchPlaceholder = searchProps?.searchPlaceholder ?? i18n[languageCode].DROPDOWN_SEARCH_PLACEHOLDER;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(PrimitiveDropdownClientSearch, {
                placeholder: searchPlaceholder
            }),
            /*#__PURE__*/ jsx(PrimitiveDropdownEmpty, {
                title: searchProps?.noFoundPlaceholderTitle,
                subtitle: searchProps?.noFoundPlaceholderSubtitle
            })
        ]
    });
}
export { GenericDropdown };
